import React from "react";
import { ScrollArea } from "../ui/scroll-area";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { ScanBarcode } from "lucide-react";
import BulkReturnScanRow from "@/components/return/BulkReturnScanRow";
import BulkReturnDetailDialog from "@/components/dialogs/BulkReturnDetailDialog";
import { RtoBulkScanReturn, RtoBulkScannedColisItem } from "@/interfaces/Return";
import {
  buildScannedCountByTournee,
  getRemainingColisForReturn,
  getReturnColisProgress,
  getScannedColisForReturn,
} from "@/utils/rto-bulk-scan";

interface BulkColisScannerProps {
  sessionReturns: RtoBulkScanReturn[];
  scannedColisItems: RtoBulkScannedColisItem[];
  scannedCount: number;
  expectedColisCount: number;
  tourneeCount: number;
  isSessionStarted: boolean;
  isScanning: boolean;
  onScan: (trackingCode: string) => Promise<boolean>;
}

const BulkColisScanner: React.FC<BulkColisScannerProps> = ({
  sessionReturns,
  scannedColisItems,
  scannedCount,
  expectedColisCount,
  tourneeCount,
  isSessionStarted,
  isScanning,
  onScan,
}) => {
  const [colisCode, setColisCode] = React.useState("");
  const [selectedReturn, setSelectedReturn] =
    React.useState<RtoBulkScanReturn | null>(null);

  const handleAddColis = async () => {
    if (!colisCode || isScanning) return;

    const success = await onScan(colisCode);
    if (success) {
      setColisCode("");
    }
  };

  const scannedCountByTournee = React.useMemo(
    () => buildScannedCountByTournee(scannedColisItems),
    [scannedColisItems]
  );

  const tourneeLabel =
    tourneeCount <= 1 ? "1 tournée" : `${tourneeCount} tournées`;

  const isComplete =
    expectedColisCount > 0 && scannedCount === expectedColisCount;

  const selectedReturnProgress = selectedReturn
    ? getReturnColisProgress(selectedReturn, scannedCountByTournee)
    : null;

  return (
    <>
      <div className="px-4 flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          {isSessionStarted ? (
            <div className="flex items-center justify-between gap-4 w-full">
              <p className="text-sm">
                <span
                  className={`font-bold tabular-nums ${
                    isComplete ? "text-emerald-600" : "text-gray-900"
                  }`}
                >
                  {scannedCount}
                </span>
                <span className="text-muted-foreground font-medium">
                  {" "}
                  / {expectedColisCount}{" "}
                </span>
                <span className="text-muted-foreground">colis scannés</span>
              </p>
              <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary shrink-0">
                {tourneeLabel}
              </span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Scannez un colis pour démarrer le ramassage
            </p>
          )}
          <div className="flex items-center justify-between gap-2">
            <Input
              placeholder="Scanner un colis"
              className="w-full h-10"
              value={colisCode}
              onChange={(e) => setColisCode(e.target.value.trim())}
              disabled={isScanning}
              onKeyDown={(e) => {
                e.key === "Enter" && handleAddColis();
              }}
            />
            <Button
              variant="outline"
              className="h-10"
              disabled={isScanning || !colisCode}
              onClick={() => handleAddColis()}
            >
              <ScanBarcode className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>
      <div className="px-4 flex flex-grow flex-col gap-4">
        <ScrollArea className="flex-1 max-h-[325px]" showScrollIndicator>
          {sessionReturns.map((return_) => {
            const progress = getReturnColisProgress(
              return_,
              scannedCountByTournee
            );

            return (
              <BulkReturnScanRow
                key={return_.numTournee}
                return_={return_}
                scannedColisCount={progress.scanned}
                expectedColisCount={progress.expected}
                status={progress.status}
                onClick={() => setSelectedReturn(return_)}
              />
            );
          })}
        </ScrollArea>
      </div>

      <BulkReturnDetailDialog
        return_={selectedReturn}
        scannedColis={
          selectedReturn
            ? getScannedColisForReturn(selectedReturn, scannedColisItems)
            : []
        }
        remainingColis={
          selectedReturn
            ? getRemainingColisForReturn(selectedReturn, scannedColisItems)
            : []
        }
        scannedColisCount={selectedReturnProgress?.scanned ?? 0}
        expectedColisCount={selectedReturnProgress?.expected ?? 0}
        isComplete={selectedReturnProgress?.isComplete ?? false}
        isOpen={selectedReturn !== null}
        onClose={() => setSelectedReturn(null)}
      />
    </>
  );
};

export default BulkColisScanner;
